import type React from "react";
import { cloneElement, useLayoutEffect, useRef, useState } from "react";

/**
 * Which side of its trigger the symbol picker's popup (symbol-picker-popup.tsx) opens on, decided once
 * when it opens. The action picker opens LEFT of its "+" instead and needs no decision here.
 *
 * Radix flips a popover to the other side as soon as it does not fit, and these popups are tall — so
 * a trigger in the lower half of the right panel used to open them UPWARD, over the designer's top
 * bar (KOA-18648 QA: the Actions "+"). Below is preferred: when the room under the trigger holds at
 * least `PICKER_MIN_ROOM`, the popup opens there and is capped to that room (the columns give up
 * height), so it fits and Radix has no reason to flip it. Only when there is less room below than
 * that, and more above, does it open upward. A host that asked for a `top*` placement keeps it.
 *
 * The cap is fixed for the whole time the popup is open, so the popup never changes height — or side
 * — under the pointer.
 *
 * The same open also reads whether the trigger sits in a modal or a popover (`role="dialog"`: KModal's
 * rc-dialog, every Radix popover) rather than in a side panel. Those hosts sit mid-screen, so the room
 * left of the trigger runs out after a column or two and Radix pushes the popup off its trigger. A
 * drawer docked to the right edge is a dialog too, but it sits where the right panel does, with the
 * canvas to its left.
 */

/** The least height the popup is usable at: search header, 160px of columns, footer. */
export const PICKER_MIN_ROOM: number = 280;

/** Radix's gap to the trigger and its viewport margin — the placement decision uses the same numbers. */
export const PICKER_SIDE_OFFSET: number = 6;
export const PICKER_COLLISION_PADDING: number = 12;

export type PickerPopupSide = "top" | "bottom";

export interface IPickerTriggerBox {
  top: number;
  bottom: number;
}

export interface IPickerPlacementInput {
  trigger: IPickerTriggerBox;
  viewportHeight: number;
  requestedSide: PickerPopupSide;
  sideOffset: number;
  collisionPadding: number;
}

export interface IPickerPlacement {
  side: PickerPopupSide;
  /** The popup's height cap on that side, in px; undefined leaves it to Radix's available height. */
  maxHeight?: number;
}

export function resolvePickerPlacement({
  trigger,
  viewportHeight,
  requestedSide,
  sideOffset,
  collisionPadding
}: IPickerPlacementInput): IPickerPlacement {
  if (requestedSide === "top") {
    return { side: "top" };
  }
  const roomBelow: number = Math.floor(viewportHeight - trigger.bottom - sideOffset - collisionPadding);
  const roomAbove: number = Math.floor(trigger.top - sideOffset - collisionPadding);
  if (roomBelow >= PICKER_MIN_ROOM || roomBelow >= roomAbove) {
    return { side: "bottom", maxHeight: Math.max(0, roomBelow) };
  }
  return { side: "top" };
}

/** The surface's style for a placement: the room below as its height cap, never past Radix's own. */
export function getPickerSurfaceStyle(placement: IPickerPlacement): React.CSSProperties | undefined {
  if (placement.maxHeight === undefined) {
    return undefined;
  }
  return {
    maxHeight: `min(${placement.maxHeight}px, calc(var(--radix-popover-content-available-height, 100vh) - var(--gap-s)))`
  };
}

function isMidScreenTrigger(element: HTMLElement): boolean {
  const dialog: Element | null = element.closest('[role="dialog"]');
  return dialog !== null && !dialog.classList.contains("kuika-drawer--right");
}

/** A trigger element as React hands it over: its own `ref`, if it has one, rides on the element. */
interface IRefCarryingElement extends React.ReactElement {
  ref?: React.Ref<HTMLElement>;
}

export interface IPickerPlacementHandle {
  placement: IPickerPlacement;
  /** The trigger with a ref that reads its box; the trigger's own ref (if any) still receives the node. */
  trigger: React.ReactElement;
  /** The trigger sits in a modal or a popover (`role="dialog"`, not a right-docked drawer), read on open. */
  isMidScreen: boolean;
}

/** Measures the trigger each time the popup opens and resolves the side (and height cap) from it. */
export function usePickerPlacement(
  open: boolean,
  requestedSide: PickerPopupSide,
  children: React.ReactElement
): IPickerPlacementHandle {
  const triggerRef: React.MutableRefObject<HTMLElement | null> = useRef<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<IPickerPlacement>({ side: requestedSide });
  const [isMidScreen, setIsMidScreen] = useState<boolean>(false);
  useLayoutEffect((): void => {
    const element: HTMLElement | null = triggerRef.current;
    if (!open || !element) {
      return;
    }
    setIsMidScreen(isMidScreenTrigger(element));
    const box: DOMRect = element.getBoundingClientRect();
    setPlacement(
      resolvePickerPlacement({
        trigger: { top: box.top, bottom: box.bottom },
        viewportHeight: window.innerHeight,
        requestedSide,
        sideOffset: PICKER_SIDE_OFFSET,
        collisionPadding: PICKER_COLLISION_PADDING
      })
    );
  }, [open, requestedSide]);

  const ownTriggerRef: React.Ref<HTMLElement> | undefined = (children as IRefCarryingElement).ref;
  const trigger: React.ReactElement = cloneElement(children, {
    ref: (element: HTMLElement | null): void => {
      triggerRef.current = element;
      if (typeof ownTriggerRef === "function") {
        ownTriggerRef(element);
      } else if (ownTriggerRef && typeof ownTriggerRef === "object") {
        (ownTriggerRef as React.MutableRefObject<HTMLElement | null>).current = element;
      }
    }
  });
  return { placement, trigger, isMidScreen };
}
